import type { CatalogOption, DocumentTypeOption, Stay } from '../../reserva/types'
import type { GuestAttachments, GuestData, GuestField } from '../types'
import type { MiDniExtractedData } from './midni/midniQrData'

/**
 * Captura de documento y cara (paso 3, 3b y 4 del check-in). Interfaz única sobre MobbScan y MiDNI
 * (inventario §4.2, D9): la pantalla pinta el marco del sistema y el SDK pinta su visor dentro del hueco.
 */
export type Side = 'front' | 'back'

export type CaptureErrorCode =
  /** El SDK no ha podido leer el documento (app actual: scanFailed) */
  | 'unreadable'
  /** Permiso de cámara denegado (noCameraAccess / miDniQr.permissionDenied) */
  | 'no_camera'
  /** Página sin HTTPS: la cámara no está disponible (miDniQr.insecureContext) */
  | 'insecure'
  /** Sin cámara o cámara ocupada (miDniQr.cameraUnavailable) */
  | 'camera_unavailable'
  /** Otro error de cámara (miDniQr.camera) */
  | 'camera'
  /** El SDK no conecta con su servidor (app actual: anErrorHasOcurred) */
  | 'connection'

export class CaptureError extends Error {
  readonly code: CaptureErrorCode
  constructor(code: CaptureErrorCode, message?: string) {
    super(message ?? code)
    this.name = 'CaptureError'
    this.code = code
  }
}

export type DocumentEvent =
  | { type: 'ready' }
  | { type: 'capturing'; side: Side }
  /** La imagen queda disponible para que la persona confirme que se ve bien antes de continuar. */
  | { type: 'captured'; side: Side; fromFile: boolean; image: Blob }
  | { type: 'error'; code: CaptureErrorCode }

export type FaceEvent = { type: 'capturing' } | { type: 'captured' } | { type: 'error'; code: CaptureErrorCode }

/** Resultado de la lectura, ya en el modelo del parte de viajeros. */
export interface DocumentReading {
  guest: GuestData
  /** Campos obligatorios que no se han podido leer: se marcan en aviso en la revisión (3b) */
  doubtful: GuestField[]
  attachments: GuestAttachments
  /** Lectura cruda del SDK, se envía tal cual al PMS en `rawData` (como hoy) */
  rawData: unknown
}

export interface ReadContext {
  stay: Pick<Stay, 'id' | 'guestCode' | 'registration'>
  documentType: DocumentTypeOption
  current: GuestData
  getMunicipalities: (provinceId: string) => Promise<CatalogOption[]>
  /** El mock devuelve a Marta (titular) o a un acompañante */
  persona: 'holder' | 'companion'
}

export interface DocumentCapture {
  /** Monta el SDK en `containerId` y empieza (MobbScan.init + start). */
  startDocument(opts: { containerId: string; documentType: DocumentTypeOption }, onEvent: (e: DocumentEvent) => void): void
  /** "Hacer la foto" del lado indicado. */
  capture(side: Side): void
  /** Muestra el siguiente lado tras confirmar la foto anterior, sin iniciar todavía una captura. */
  showSide(side: Side): void
  /** "Subir una foto": selector de archivo del propio SDK (D9). */
  pickFile(side: Side): void
  /** Escritorio: entrega un archivo soltado al input del SDK. false si no hay input. */
  giveFile(side: Side, file: File): boolean
  /** Lados que faltan por capturar. */
  missingSides(): Side[]
  /** OCR de lo capturado (MobbScan.scan) y adaptación al parte. Rechaza CaptureError('unreadable'). */
  read(ctx: ReadContext): Promise<DocumentReading>
  /** Paso 4: muestra la captura de cara (hideDocumentsAndShowFace). */
  startFace(onEvent: (e: FaceEvent) => void): void
  captureFace(): void
  /** Compara la cara con el documento (MobbScan.validate FACE). */
  validateFace(): Promise<{ valid: boolean; face: string }>
  stop(): void
}

/** MiDNI: lectura continua del QR con qr-scanner y parseo local (app actual). Devuelve la función para parar. */
export interface QrCapture {
  start(video: HTMLVideoElement, reserveId: string, onDetected: (data: MiDniExtractedData) => void, onError: (code: CaptureErrorCode) => void): Promise<() => void>
}
